'use client'

import { useParams } from 'next/navigation'
import { useEffect, useMemo, useState } from 'react'
import { Locale, Namespace, isRTLLanguage } from '@/lib/i18n/config'
import { loadTranslation, getTranslation, getCachedTranslation } from '@/lib/i18n/translations'
import { useTranslationContext } from '@/components/translation-provider'

export function useTranslation(namespace: Namespace) {
  const params = useParams()
  const locale = (params?.locale as Locale) || 'en'
  const ctx = useTranslationContext()

  // If server-loaded translations are available via context, use them synchronously
  const serverTranslations = ctx?.translations?.[namespace]

  const [translations, setTranslations] = useState<Record<string, any>>(
    () => serverTranslations || getCachedTranslation(locale, namespace) || {}
  )
  const [isLoading, setIsLoading] = useState(
    () => !serverTranslations && !getCachedTranslation(locale, namespace)
  )

  useEffect(() => {
    // If we already have translations from context, skip async loading
    if (serverTranslations) {
      setTranslations(serverTranslations)
      setIsLoading(false)
      return
    }
    const cached = getCachedTranslation(locale, namespace)
    if (cached) {
      setTranslations(cached)
      setIsLoading(false)
      return
    }
    const load = async () => {
      setIsLoading(true)
      try {
        const trans = await loadTranslation(locale, namespace)
        setTranslations(trans)
      } catch (error) {
        console.error('Failed to load translations:', error)
      } finally {
        setIsLoading(false)
      }
    }

    load()
  }, [locale, namespace, serverTranslations])

  const t = (key: string, params?: Record<string, string | number | boolean> & { returnObjects?: boolean }) => {
    if (isLoading) return key
    return getTranslation(translations, key, params)
  }

  const isRTL = isRTLLanguage(locale)

  return { t, locale, isLoading, isRTL }
}

// Hook for multiple namespaces
export function useTranslations(namespaces: Namespace[]) {
  const params = useParams()
  const locale = (params?.locale as Locale) || 'en'
  const ctx = useTranslationContext()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sortedNamespaces = useMemo(() => [...namespaces].sort(), [namespaces.join(',')])

  // Only re-run the effect when translations for our specific namespaces change
  const relevantTranslations = useMemo(() => {
    if (!ctx?.translations) return null
    const slice: Record<string, any> = {}
    for (const ns of sortedNamespaces) {
      if (ctx.translations[ns]) {
        slice[ns] = ctx.translations[ns]
      }
    }
    return Object.keys(slice).length > 0 ? slice : null
  }, [ctx?.translations, sortedNamespaces])

  const [translations, setTranslations] = useState<Record<string, any>>(() => {
    if (ctx?.translations) {
      const initial: Record<string, any> = {}
      let hasAll = true
      for (const ns of namespaces) {
        if (ctx.translations[ns]) {
          initial[ns] = ctx.translations[ns]
        } else {
          hasAll = false
        }
      }
      if (hasAll) return initial
    }
    return {}
  })
  const [isLoading, setIsLoading] = useState(
    () => {
      if (!ctx?.translations) return true
      return !namespaces.every(ns => !!ctx.translations[ns])
    }
  )

  useEffect(() => {
    // Use the pre-computed relevant translations slice
    if (relevantTranslations) {
      const hasAll = sortedNamespaces.every(ns => !!relevantTranslations[ns])
      if (hasAll) {
        setTranslations(relevantTranslations)
        setIsLoading(false)
        return
      }
    }

    const load = async () => {
      setIsLoading(true)
      try {
        const allTranslations: Record<string, any> = {}

        await Promise.all(
          sortedNamespaces.map(async (namespace) => {
            const trans = await loadTranslation(locale, namespace)
            allTranslations[namespace] = trans
          })
        )

        setTranslations(allTranslations)
      } catch (error) {
        console.error('Failed to load translations:', error)
      } finally {
        setIsLoading(false)
      }
    }

    load()
  }, [locale, sortedNamespaces, relevantTranslations])

  const t = (key: string, params?: Record<string, string | number | boolean> & { returnObjects?: boolean }) => {
    if (isLoading) return key

    // Handle namespace.key format
    const [namespace, ...keyParts] = key.split('.')
    const actualKey = keyParts.join('.')

    if (translations[namespace]) {
      return getTranslation(translations[namespace], actualKey, params)
    }

    // If no namespace, try all loaded namespaces in deterministic order
    for (const nsKey of sortedNamespaces) {
      if (translations[nsKey]) {
        const value = getTranslation(translations[nsKey], key, params)
        if (value !== key) return value
      }
    }

    return key
  }

  const isRTL = isRTLLanguage(locale)

  return { t, locale, isLoading, isRTL }
}
